import axios from "axios";

// Separate axios instance for the clinic module's patient-facing mount
// (/clinic/patient/...) — a different URL prefix than the portal mount
// client.js talks to, but the SAME PortalTokenAuthentication token, so
// this mirrors client.js's interceptor pattern exactly rather than
// sharing one axios instance across two base URLs.
const BASE_URL = process.env.REACT_APP_CLINIC_BASE_URL || "http://localhost:8000/clinic/patient/";
// Token refresh always goes through the portal mount — the clinic mount
// has no refresh endpoint of its own, it's the same CustomerAccount session.
const REFRESH_URL = process.env.REACT_APP_BASE_URL || "http://localhost:8000/portal/";

// doctor_photo_url (and any other /media/... path returned by the API)
// is relative, same reasoning as clinic-frontend's api/client.js —
// prefix with the API's own origin so <img src> works regardless of
// whether this frontend is served from a different origin than the API.
export const MEDIA_BASE_URL = new URL(BASE_URL).origin;

const clinicApi = axios.create({
  baseURL: BASE_URL,
});

clinicApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers["Authorization"] = `Bearer ${token}`;
  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const forceLogout = () => {
  localStorage.clear();
  window.location.href = "/login";
};

clinicApi.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return clinicApi(originalRequest);
        }).catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem("refresh_token");

      if (!refreshToken) {
        forceLogout();
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(`${REFRESH_URL}auth/token/refresh/`, {
          refresh_token: refreshToken,
        });

        const newAccessToken = response.data.token;
        const newRefreshToken = response.data.refresh_token;

        localStorage.setItem("token", newAccessToken);
        if (newRefreshToken) {
          localStorage.setItem("refresh_token", newRefreshToken);
        }

        clinicApi.defaults.headers["Authorization"] = `Bearer ${newAccessToken}`;
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);
        return clinicApi(originalRequest);

      } catch (refreshError) {

        processQueue(refreshError, null);
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default clinicApi;
